import dayjs from 'dayjs';

import {
  FULL_DAY_OF_WEEK_FORMAT,
  FULL_MONTH_FORMAT,
  FULL_YEAR_FORMAT,
  SHORT_DATE_FORMAT,
} from '@/common/constants';

import { ScheduleDate, ScheduleYear } from '../payrollCalendarSection.types';

// A schedule date enriched with its month label, used only while grouping.
interface ScheduleDateWithMonth extends ScheduleDate {
  monthLabel: string;
}

/**
 * Groups a flat list of schedule dates into years -> months for the card's read-only
 * view. Month label comes from dayjs; the numeric month index is kept only for sorting.
 */
export const groupScheduleByYearMonth = (dates?: string[]): ScheduleYear[] => {
  if (!dates || dates.length === 0) return [];

  const yearMap = new Map<string, Map<number, ScheduleDateWithMonth[]>>();

  dates.forEach((dateStr) => {
    const d = dayjs(dateStr);
    if (!d.isValid()) return;

    const year = d.format(FULL_YEAR_FORMAT);
    const month = d.month();
    const entry: ScheduleDateWithMonth = {
      monthLabel: d.format(FULL_MONTH_FORMAT),
      formatted: d.format(SHORT_DATE_FORMAT),
      dayOfWeek: d.format(FULL_DAY_OF_WEEK_FORMAT),
    };

    if (!yearMap.has(year)) {
      yearMap.set(year, new Map());
    }
    const monthMap = yearMap.get(year)!;
    if (!monthMap.has(month)) {
      monthMap.set(month, []);
    }
    monthMap.get(month)!.push(entry);
  });

  return Array.from(yearMap.keys())
    .sort((a, b) => Number(a) - Number(b))
    .map((year) => {
      const monthMap = yearMap.get(year)!;
      const months = Array.from(monthMap.keys())
        .sort((a, b) => a - b)
        .map((monthIndex) => {
          const dateEntries = monthMap.get(monthIndex)!;
          return {
            monthLabel: dateEntries[0].monthLabel,
            count: dateEntries.length,
            dates: dateEntries.map(({ formatted, dayOfWeek }) => ({ formatted, dayOfWeek })),
          };
        });

      return { year, months };
    });
};

export default groupScheduleByYearMonth;
